import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useVehicleSettingsOperations } from "../../../hooks/useVehicleSettingsOperations";
import { useNotification } from "../../../context/NotificationContext";
import LoadingAnimation from "../../loading/LoadingAnimation";
import { useLayout } from "../../../context/LayoutContext";
import "../../../assets/scss/components/ui/tables/SatelliteProviderSettings.scss"

// Componente de Switch personalizado
function CustomSwitch({ checked, onChange, theme, id }) {
  return (
    <label className={`SPS__${theme}-switch`} htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={`SPS__${theme}-switch-slider`}></span>
    </label>
  );
}

CustomSwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired
};

// Componente para seleccionar días de la semana
function DaysSelector({ value, onChange, theme, id }) {
  const daysOfWeek = [
    { value: 0, label: 'D' },
    { value: 1, label: 'L' },
    { value: 2, label: 'M' },
    { value: 3, label: 'X' },
    { value: 4, label: 'J' },
    { value: 5, label: 'V' },
    { value: 6, label: 'S' }
  ];
  
  const selectedDays = value.split(',').map(Number);
  
  const handleToggleDay = (day) => {
    if (selectedDays.includes(day)) {
      const newDays = selectedDays.filter(d => d !== day);
      onChange(newDays.join(','));
    } else {
      const newDays = [...selectedDays, day].sort((a, b) => a - b);
      onChange(newDays.join(','));
    }
  };
  
  return (
    <div className={`SPS__${theme}-days-selector`} id={id} role="group" aria-label="Seleccionar días de la semana">
      {daysOfWeek.map((day) => (
        <button
          key={day.value}
          type="button"
          className={`SPS__${theme}-day-chip ${selectedDays.includes(day.value) ? 'active' : ''}`}
          onClick={() => handleToggleDay(day.value)}
          aria-pressed={selectedDays.includes(day.value)}
        >
          {day.label}
        </button>
      ))}
    </div>
  );
}

DaysSelector.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired
};

// Componente de Slider personalizado
function CustomTimeSlider({ value, onChange, min = 0, max = 24, theme, id }) {
  const handleChange = (index, newValue) => {
    const updatedValue = [...value];
    updatedValue[index] = parseInt(newValue);
    onChange(updatedValue);
  };

  return (
    <div className={`SPS__${theme}-time-slider-container`} id={id}>
      <span className={`SPS__${theme}-time-value`}>{value[0]}:00</span>
      <div className={`SPS__${theme}-dual-range-slider`}>
        <input
          id={`${id}-min`}
          type="range"
          min={min}
          max={max}
          value={value[0]}
          onChange={(e) => handleChange(0, e.target.value)}
          className={`SPS__${theme}-range-input min`}
          aria-label={`Hora de inicio: ${value[0]}:00`}
        />
        <input
          id={`${id}-max`}
          type="range"
          min={min}
          max={max}
          value={value[1]}
          onChange={(e) => handleChange(1, e.target.value)}
          className={`SPS__${theme}-range-input max`}
          aria-label={`Hora de fin: ${value[1]}:00`}
        />
        <div className={`SPS__${theme}-slider-track`}></div>
        <div 
          className={`SPS__${theme}-slider-range`}
          style={{
            left: `${(value[0] / max) * 100}%`,
            right: `${100 - (value[1] / max) * 100}%`
          }}
        ></div>
      </div>
      <span className={`SPS__${theme}-time-value`}>{value[1]}:00</span>
    </div>
  );
}

CustomTimeSlider.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  theme: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired
};

// Modal de destinatario actualizado para incluir teléfono
function RecipientModal({ open, onClose, onSubmit, initialValue = null, theme }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (initialValue) {
      if (typeof initialValue === 'string') {
        // Retrocompatibilidad con strings de email
        setEmail(initialValue);
        setPhone('');
      } else {
        // Manejo de objetos con email y phone
        setEmail(initialValue.email || '');
        setPhone(initialValue.phone || '');
      }
    } else {
      setEmail('');
      setPhone('');
    }
    setError('');
  }, [initialValue, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-()]+$/;
    
    // Validación de email (obligatorio)
    if (!emailRegex.test(email)) {
      setError('Correo electrónico inválido');
      return;
    }
    
    // Validación de teléfono (opcional)
    if (phone && !phoneRegex.test(phone)) {
      setError('Número de teléfono inválido');
      return;
    }
    
    // Envío de datos como objeto
    onSubmit({ email, phone: phone || null });
    setEmail('');
    setPhone('');
    setError('');
  };

  if (!open) return null;

  return (
    <div className={`SPS__${theme}-modal-overlay`}>
      <div className={`SPS__${theme}-modal-content`}>
        <div className={`SPS__${theme}-modal-header`}>
          <h3>{initialValue ? 'Editar Destinatario' : 'Añadir Destinatario'}</h3>
          <button 
            type="button"
            className={`SPS__${theme}-modal-close`}
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={`SPS__${theme}-form-group`}>
            <label htmlFor="recipient-email">Correo Electrónico *</label>
            <input
              id="recipient-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              required
              className={`SPS__${theme}-form-input`}
            />
          </div>
          <div className={`SPS__${theme}-form-group`}>
            <label htmlFor="recipient-phone">Teléfono WhatsApp (opcional)</label>
            <input
              id="recipient-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+57 300 123 4567"
              className={`SPS__${theme}-form-input`}
            />
            <small className={`SPS__${theme}-form-hint`}>
              Incluir código de país para notificaciones WhatsApp
            </small>
          </div>
          {error && <div className={`SPS__${theme}-form-error`}>{error}</div>}
          <div className={`SPS__${theme}-modal-actions`}>
            <button 
              type="button" 
              onClick={onClose} 
              className={`SPS__${theme}-btn-cancel`}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className={`SPS__${theme}-btn-save`}
            >
              {initialValue ? 'Actualizar' : 'Añadir'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

RecipientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  theme: PropTypes.string.isRequired
};

export function SatelliteProviderSettings() {
  const [activeTab, setActiveTab] = useState("general");
  const [provider, setProvider] = useState('securita');
  const [editableConfig, setEditableConfig] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [currentRecipient, setCurrentRecipient] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { 
    useSatelliteProviders,
    useProviderConfig,
    useUpdateProviderConfig,
    useUpdateProviderRecipients,
    useProcessNotifications
  } = useVehicleSettingsOperations();
  
  // Obtener lista de proveedores dinámicamente
  const { data: providersData, isLoading: providersLoading } = useSatelliteProviders();
  const { data: configData, isLoading: configLoading, refetch: refetchConfig } = useProviderConfig(provider);
  
  const updateConfigMutation = useUpdateProviderConfig();
  const updateRecipientsMutation = useUpdateProviderRecipients();
  const processNotificationsMutation = useProcessNotifications();
  
  const { showNotification } = useNotification();
  const { theme } = useLayout();
  
  useEffect(() => {
    if (configData?.data) {
      const { config, recipients } = configData.data;
      setEditableConfig({ ...config });
      setRecipients(recipients || []);
    }
  }, [configData]);
  
  const handleChange = (field, value) => {
    setEditableConfig({
      ...editableConfig,
      [field]: value
    });
  };

  const handleSubmitConfig = async () => {
    try {
      await updateConfigMutation.mutateAsync({
        providerName: provider,
        configData: editableConfig
      });
      refetchConfig();
      showNotification({
        message: "Configuración actualizada correctamente",
        severity: "success"
      });
    } catch (error) {
      showNotification({
        message: `Error al actualizar configuración: ${error.message}`,
        severity: "error"
      });
    }
  };

  const handleAddRecipient = () => {
    setCurrentRecipient(null);
    setRecipientModalOpen(true);
  };

  // Actualizado para manejar objetos y strings
  const handleEditRecipient = (recipient) => {
    setCurrentRecipient(recipient);
    setRecipientModalOpen(true);
  };

  const handleDeleteRecipient = (emailToDelete) => {
    if (window.confirm(`¿Eliminar el destinatario ${emailToDelete}?`)) {
      const updatedRecipients = recipients.filter(recipient => {
        if (typeof recipient === 'string') {
          return recipient !== emailToDelete;
        }
        return recipient.email !== emailToDelete;
      });
      updateRecipients(updatedRecipients);
    }
  };

  // Actualizado para manejar objetos con email y phone
  const handleSubmitRecipient = (recipientData) => {
    let updatedRecipients;
    
    if (currentRecipient) {
      // Edición de destinatario existente
      updatedRecipients = recipients.map(r => {
        if (typeof r === 'string' && r === currentRecipient) {
          return recipientData;
        } else if (typeof r === 'object' && r.email === currentRecipient.email) {
          return recipientData;
        }
        return r;
      });
    } else {
      // Nuevo destinatario - verificar duplicados
      const emailExists = recipients.some(r => {
        if (typeof r === 'string') return r === recipientData.email;
        return r.email === recipientData.email;
      });
      
      if (emailExists) {
        showNotification({
          message: "Este correo ya está en la lista de destinatarios",
          severity: "warning"
        });
        setRecipientModalOpen(false);
        return;
      }
      
      updatedRecipients = [...recipients, recipientData];
    }
    
    updateRecipients(updatedRecipients);
    setRecipientModalOpen(false);
  };

  const updateRecipients = async (newRecipients) => {
    try {
      await updateRecipientsMutation.mutateAsync({
        providerName: provider,
        recipients: newRecipients
      });
      setRecipients(newRecipients);
      showNotification({
        message: "Destinatarios actualizados correctamente",
        severity: "success"
      });
    } catch (error) {
      showNotification({
        message: `Error al actualizar destinatarios: ${error.message}`,
        severity: "error"
      });
    }
  };

  const handleProcessNotifications = async () => {
    setIsProcessing(true);
    try {
      await processNotificationsMutation.mutateAsync(provider);
      showNotification({
        message: "Notificaciones procesadas correctamente",
        severity: "success"
      });
    } catch (error) {
      showNotification({
        message: `Error al procesar notificaciones: ${error.message}`,
        severity: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (configLoading || providersLoading) {
    return <LoadingAnimation />;
  }

  if (!editableConfig) {
    return (
      <div className={`SPS__${theme}-error-message`}>
        No se pudo cargar la configuración del proveedor. Intente de nuevo más tarde.
      </div>
    );
  }

  return (
    <div className={`SPS__${theme}-container`}>
      <header className={`SPS__${theme}-header`}>
        <h2 className={`SPS__${theme}-title`}>Configuración de Proveedores Satelitales</h2>
        
        <div className={`SPS__${theme}-header-controls`}>
          <label htmlFor="provider-select" className="sr-only">Seleccionar proveedor</label>
          {/* Select actualizado para usar proveedores dinámicos */}
          <select
            id="provider-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className={`SPS__${theme}-provider-select`}
          >
            {providersData?.data?.map(prov => (
              <option key={prov.id} value={prov.provider_name}>
                {prov.provider_name.toUpperCase()}
              </option>
            ))}
          </select>
          
          <button
            type="button"
            className={`SPS__${theme}-process-button`}
            onClick={handleProcessNotifications}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className={`SPS__${theme}-processing`}>
                <i className="fas fa-spinner fa-spin"></i>
                Procesando...
              </span>
            ) : (
              <>
                <i className="fas fa-sync-alt"></i>
                Procesar Notificaciones
              </>
            )}
          </button>
        </div>
      </header>
      
      <nav className={`SPS__${theme}-navigation`}>
        <button
          type="button"
          className={`SPS__${theme}-nav-button ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          <i className="fas fa-cog"></i>
          Configuración General
        </button>
        <button
          type="button"
          className={`SPS__${theme}-nav-button ${activeTab === "recipients" ? "active" : ""}`}
          onClick={() => setActiveTab("recipients")}
        >
          <i className="fas fa-users"></i>
          Destinatarios
        </button>
        <button
          type="button"
          className={`SPS__${theme}-nav-button ${activeTab === "parameters" ? "active" : ""}`}
          onClick={() => setActiveTab("parameters")}
        >
          <i className="fas fa-sliders-h"></i>
          Parámetros de Eventos
        </button>
      </nav>
      
      <div className={`SPS__${theme}-content`}>
        {/* Panel de Configuración General */}
        {activeTab === "general" && (
          <section className={`SPS__${theme}-general-section`}>
            <div className={`SPS__${theme}-config-item`}>
              <label htmlFor="notification-enabled" className={`SPS__${theme}-config-label`}>
                Notificaciones habilitadas
              </label>
              <CustomSwitch
                id="notification-enabled"
                checked={editableConfig.notification_enabled}
                onChange={(value) => handleChange('notification_enabled', value)}
                theme={theme}
              />
            </div>
            
            <div className={`SPS__${theme}-config-item full-width`}>
              <label htmlFor="notification-hours" className={`SPS__${theme}-config-label`}>
                Horario de notificaciones
              </label>
              <CustomTimeSlider
                id="notification-hours"
                value={[editableConfig.notification_start_hour, editableConfig.notification_end_hour]}
                onChange={(newValue) => {
                  handleChange('notification_start_hour', newValue[0]);
                  handleChange('notification_end_hour', newValue[1]);
                }}
                theme={theme}
              />
            </div>
            
            <div className={`SPS__${theme}-config-item full-width`}>
              <label htmlFor="notification-days" className={`SPS__${theme}-config-label`}>
                Días de notificación
              </label>
              <DaysSelector 
                id="notification-days"
                value={editableConfig.notification_days} 
                onChange={(value) => handleChange('notification_days', value)}
                theme={theme}
              />
            </div>
            
            <button
              type="button"
              className={`SPS__${theme}-save-button`}
              onClick={handleSubmitConfig}
              disabled={updateConfigMutation.isLoading}
            >
              {updateConfigMutation.isLoading ? 
                'Guardando...' : 'Guardar Configuración'}
            </button>
          </section>
        )}
        
        {/* Panel de Destinatarios actualizado */}
        {activeTab === "recipients" && (
          <section className={`SPS__${theme}-recipients-section`}>
            <div className={`SPS__${theme}-recipients-header`}>
              <h3 className={`SPS__${theme}-section-title`}>
                Destinatarios de Notificaciones
              </h3>
              <button
                type="button"
                className={`SPS__${theme}-add-button`}
                onClick={handleAddRecipient}
              >
                <i className="fas fa-plus"></i>
                Agregar Destinatario
              </button>
            </div>
            
            <div className={`SPS__${theme}-recipients-list`}>
              {recipients.length === 0 ? (
                <div className={`SPS__${theme}-empty-message`}>
                  No hay destinatarios configurados para recibir notificaciones.
                </div>
              ) : (
                recipients.map((recipient) => {
                  // Normalizar destinatario a objeto
                  const recipientObj = typeof recipient === 'string' 
                    ? { email: recipient, phone: null } 
                    : recipient;
                    
                  return (
                    <div key={recipientObj.email} className={`SPS__${theme}-recipient-item`}>
                      <div className={`SPS__${theme}-recipient-info`}>
                        <i className="fas fa-envelope"></i>
                        <span>{recipientObj.email}</span>
                        {recipientObj.phone && (
                          <>
                            <i className="fas fa-phone" style={{ marginLeft: '10px' }}></i>
                            <span>{recipientObj.phone}</span>
                          </>
                        )}
                      </div>
                      <div className={`SPS__${theme}-recipient-actions`}>
                        <button
                          type="button"
                          className={`SPS__${theme}-icon-button edit`}
                          onClick={() => handleEditRecipient(recipientObj)}
                          aria-label={`Editar destinatario ${recipientObj.email}`}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          className={`SPS__${theme}-icon-button delete`}
                          onClick={() => handleDeleteRecipient(recipientObj.email)}
                          aria-label={`Eliminar destinatario ${recipientObj.email}`}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}
        
        {/* Panel de Parámetros de Eventos */}
        {activeTab === "parameters" && (
          <section className={`SPS__${theme}-parameters-section`}>
            <h3 className={`SPS__${theme}-section-title`}>
              Umbrales de Detección
            </h3>
            
            <div className={`SPS__${theme}-parameters-grid`}>
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="speed-threshold" className={`SPS__${theme}-config-label`}>
                  Umbral de velocidad (km/h)
                </label>
                <input
                  id="speed-threshold"
                  type="number"
                  min="0"
                  value={editableConfig.speed_threshold}
                  onChange={(e) => handleChange('speed_threshold', parseFloat(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="critical-point-speed-threshold" className={`SPS__${theme}-config-label`}>
                  Umbral en puntos críticos (km/h)
                </label>
                <input
                  id="critical-point-speed-threshold"
                  type="number"
                  min="0"
                  value={editableConfig.critical_point_speed_threshold}
                  onChange={(e) => handleChange('critical_point_speed_threshold', parseFloat(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="consecutive-overspeeds" className={`SPS__${theme}-config-label`}>
                  Excesos consecutivos requeridos
                </label>
                <input
                  id="consecutive-overspeeds"
                  type="number"
                  min="1"
                  value={editableConfig.consecutive_overspeeds_required}
                  onChange={(e) => handleChange('consecutive_overspeeds_required', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="min-distance-events" className={`SPS__${theme}-config-label`}>
                  Distancia mínima entre eventos (m)
                </label>
                <input
                  id="min-distance-events"
                  type="number"
                  min="0"
                  value={editableConfig.min_distance_between_events}
                  onChange={(e) => handleChange('min_distance_between_events', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
            </div>
            
            <h3 className={`SPS__${theme}-section-title`}>
              Ventanas de tiempo para eventos (minutos)
            </h3>
            
            <div className={`SPS__${theme}-parameters-grid`}>
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="overspeed-time-window" className={`SPS__${theme}-config-label`}>
                  Exceso de velocidad
                </label>
                <input
                  id="overspeed-time-window"
                  type="number"
                  min="1"
                  value={editableConfig.overspeed_time_window}
                  onChange={(e) => handleChange('overspeed_time_window', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="harsh-event-time-window" className={`SPS__${theme}-config-label`}>
                  Eventos bruscos
                </label>
                <input
                  id="harsh-event-time-window"
                  type="number"
                  min="1"
                  value={editableConfig.harsh_event_time_window}
                  onChange={(e) => handleChange('harsh_event_time_window', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="jamming-time-window" className={`SPS__${theme}-config-label`}>
                  Jamming
                </label>
                <input
                  id="jamming-time-window"
                  type="number"
                  min="1"
                  value={editableConfig.jamming_time_window}
                  onChange={(e) => handleChange('jamming_time_window', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="low-speed-time-window" className={`SPS__${theme}-config-label`}>
                  Baja velocidad
                </label>
                <input
                  id="low-speed-time-window"
                  type="number"
                  min="1"
                  value={editableConfig.low_speed_time_window}
                  onChange={(e) => handleChange('low_speed_time_window', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
              
              <div className={`SPS__${theme}-config-item`}>
                <label htmlFor="distance-report-time-window" className={`SPS__${theme}-config-label`}>
                  Reportes de distancia
                </label>
                <input
                  id="distance-report-time-window"
                  type="number"
                  min="1"
                  value={editableConfig.distance_report_time_window}
                  onChange={(e) => handleChange('distance_report_time_window', parseInt(e.target.value))}
                  className={`SPS__${theme}-form-input`}
                />
              </div>
            </div>
            
            <button
              type="button"
              className={`SPS__${theme}-save-button`}
              onClick={handleSubmitConfig}
              disabled={updateConfigMutation.isLoading}
            >
              {updateConfigMutation.isLoading ? 
                'Guardando...' : 'Guardar Configuración'}
            </button>
          </section>
        )}
      </div>
      
      {/* Modal actualizado */}
      <RecipientModal
        open={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
        onSubmit={handleSubmitRecipient}
        initialValue={currentRecipient}
        theme={theme}
      />
    </div>
  );
}